import { Dispatch, SetStateAction, useEffect } from 'react'
import Client from 'fhirclient/lib/Client'
import { CdsCard, CdsDebugInfo, HeaderItem } from './cds.types'
import { Actions, useAdministrationStateContext } from '../../components/state'
import { CdsRequest, CdsResponse } from '../'

export type CdsHandlerProps = {
    client: Client
    setCdsCards: Dispatch<SetStateAction<CdsCard[]>>
    selectedAdministrationId: number | undefined
    allowVerboseCards: boolean
    chartCheck: boolean
}

export function CdsHandler({ client, setCdsCards, selectedAdministrationId, allowVerboseCards, chartCheck }: CdsHandlerProps) {
    const { globalState: state, dispatch } = useAdministrationStateContext()

    const refreshCards = () => {
        if (state.servicesLoaded
            && state.prefetchLoaded
            && state.medicationPopulated
            && selectedAdministrationId !== undefined) {
            if (state.cdsServices.length === 0) {
                console.error('No CDS Services present')
            }
            setCdsCards([])

            let cdsDebugInfo = [] as CdsDebugInfo[]

            if (state.medicationRequest.dosageInstruction) {
                state.cdsServices.forEach(async function (service) {
                    let cdsRequest = {} as CdsRequest

                    cdsRequest.hookInstance = state.hookInstanceId
                    cdsRequest.hook = 'order-select'
                    cdsRequest.fhirServer = client.state.serverUrl
                    cdsRequest.context = {
                        patientId: client.patient.id as string,
                        userId: client.user.id as string,
                        encounterId: client.encounter.id as string,
                        selections: ['MedicationRequest/request-123'],
                        draftOrders: {
                            resourceType: 'Bundle', entry: [{
                                resource:
                                    state.medicationRequest
                            }]
                        }
                    }
                    cdsRequest.prefetch = service.populatedPrefetch
                    const verboseCardsQueryParam = allowVerboseCards ? '?verbose-cards=true' : ''
                    if ((service.id === 'cumulative-dose-check' || service.id === 'cumulative-dose-chart-check')) {
                        service.id = chartCheck ? 'cumulative-dose-chart-check' : 'cumulative-dose-check'
                    }

                    const url = `${service.url}/${service.id}${verboseCardsQueryParam}`

                    let response = {}
                    await http<CdsResponse>(url, cdsRequest, service.headers)
                        .then((data) => {
                            if (data.cards) {
                                const newCards = data.cards.map((card) => {
                                    const currentMedication = state.medicationRequest

                                    return {
                                        ...card,
                                        requestId: currentMedication.id,
                                        serviceUrl: url,
                                        summary: card.summary,
                                        administrationDetails: {
                                            doseQuantity: currentMedication.dosageInstruction.doseAndRate.doseQuantity.value,
                                            route: currentMedication.dosageInstruction.route.text,
                                            unit: currentMedication.dosageInstruction.doseAndRate.doseQuantity.unit,
                                            text: currentMedication.medicationCodeableConcept.text,
                                            timing: currentMedication.dosageInstruction.timing.event
                                        }
                                    } as CdsCard
                                })
                                setCdsCards(newCards)
                                response = data
                            } else {
                                if (data.statusCode === 412) {
                                    const currentMedication = state.medicationRequest

                                    const card: any = currentMedication ? {
                                        requestId: currentMedication.id,
                                        summary: '412 Precondition Failed',
                                        administrationDetails: {
                                            doseQuantity: currentMedication.dosageInstruction.doseAndRate.doseQuantity.value,
                                            route: currentMedication.dosageInstruction.route.text,
                                            unit: currentMedication.dosageInstruction.doseAndRate.doseQuantity.unit,
                                            text: currentMedication.medicationCodeableConcept.text,
                                            timing: currentMedication.dosageInstruction.timing.event
                                        },
                                        detail: data.information,
                                        source: '',
                                        serviceUrl: url,
                                        indicator: 'warning',
                                        suggestions: '',
                                        links: '',
                                        selectionBehavior: ''
                                    } as CdsCard :
                                        {
                                            summary: '412 Precondition Failed',
                                            detail: data.information,
                                            source: '',
                                            serviceUrl: url,
                                            indicator: 'warning',
                                            suggestions: '',
                                            links: '',
                                            selectionBehavior: ''
                                        } as CdsCard
                                    setCdsCards([card])
                                } else if (data.information) {
                                    dispatch(Actions.AddError({ message: `CDS service ${service.id} resulted in error`, stacktrace: data.information }))
                                }
                            }
                        })
                        .catch((error) => {
                            dispatch(Actions.AddError({ message: `Error occured calling CDS service ${service.id}`, stacktrace: error.message }))
                            response = error.message
                        })
                        .finally(() => {
                            cdsDebugInfo.push({ serviceUrl: url, request: cdsRequest, response: response } as CdsDebugInfo)
                            dispatch(Actions.SetServerDebugInfo(cdsDebugInfo))
                        })
                })
            }
        } else {
            console.log('Waiting for services to load before displaying cards')
        }
    }

    async function http<T>(url: string, body: any, headerData: HeaderItem[]): Promise<T> {
        const request = JSON.stringify(body)
        const headers = new Headers({ 'Content-Type': 'application/json' })
        headerData.forEach(headerItem => {
            headers.append(headerItem.key, headerItem.value)
        })
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: request
        })
        const responseBody = await response.json()
        return responseBody
    }

    useEffect(() => {
        if (state.prefetchLoaded) {
            refreshCards()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.prefetchLoaded])

    return <></>
}